import { useEffect, useRef } from "react";
import { ErrorBoundary } from "react-error-boundary";

import styled from "styled-components/macro";

import NoteInfo from "Payments/PaymentPage/NoteInfo";
import { mockData } from "Payments/PaymentPage/mockData";
import { usePaymentMethodProviders } from "Payments/globalPaymentsStateHooks";
import { motion } from "framer-motion";

import ErrorBoundaryFallback from "./ErrorBoundaryFallback";
import { ForcePageReloadModal } from "./ForcePageReloadModal";
import GradientBackground from "./GradientBackground";
import PageHeader from "./PageHeader";
import { pageWidth } from "./PaymentFormScreen/styles.js";
import { PaymentPageProvider, usePaymentPage } from "./PaymentPageContext";
import PaymentScreenRouter from "./PaymentScreenRouter";
import { paymentScreens } from "./PaymentScreenRouter/paymentScreens";
import { useGenerateAndSetTabID } from "./useGenerateAndSetTabID";

function PaymentPage({ paymentParent }) {
  const {
    state: {
      currentScreen,
      fund: { fundColorSafe, description },
    },
    dispatch,
  } = usePaymentPage();
  useGenerateAndSetTabID();
  const { setPaymentMethodProviders } = usePaymentMethodProviders();

  useEffect(
    function handleInitialData() {
      // The initial page load includes the page data we need in the window.data object.
      let paymentPageData = window.data?.paymentPageData;

      if (mockData.shouldMock) {
        paymentPageData = mockData.paymentPageData;
      }

      dispatch({
        type: "handleWindowData",
        payload: paymentPageData,
      });

      setPaymentMethodProviders(paymentPageData?.paymentMethodProviders);

      // sets paymentParent is context, which is used for styling and actions when PaymentsPage is used outside of the FundIt page, like for Signup payments
      if (paymentParent) {
        dispatch({
          type: "handlePaymentParent",
          payload: paymentParent,
        });
      }
    },
    [dispatch, paymentParent]
  );

  useEffect(
    function setDocumentTitle() {
      if (description) document.title = `Give to ${description}`;
    },
    [description]
  );

  const containerRef = useRef();

  return (
    <ErrorBoundary FallbackComponent={ErrorBoundaryFallback}>
      <GradientBackground
        hideDefaultBackground={paymentParent?.hideDefaultBackground}
      />
      <Content
        hideDefaultBackground={paymentParent?.hideDefaultBackground}
        data-cy="PaymentPage_Container"
      >
        {!paymentParent || currentScreen === paymentScreens.success ? (
          <PageHeader />
        ) : null}
        <CardContainer
          ref={containerRef}
          style={{
            "--fundColor": paymentParent?.themeColor
              ? paymentParent.themeColor
              : fundColorSafe,
            opacity: currentScreen ? 1 : 0, // hides initial white box flash before currentScreen gets set
          }}
          $hideCardStyles={
            paymentParent?.hideCardStyles && currentScreen !== "success"
          }
        >
          <NoteInfo />
          <PaymentScreenRouter containerRef={containerRef} />
        </CardContainer>
      </Content>
      <ForcePageReloadModal />
    </ErrorBoundary>
  );
}

export default function PaymentPageWithProvider({ paymentParent = null }) {
  // this allows us to access PaymentPageContext in the PaymentPage component.
  return (
    <PaymentPageProvider>
      <PaymentPage paymentParent={paymentParent} />
    </PaymentPageProvider>
  );
}

const Content = styled.div`
  min-height: ${(props) => (props.hideDefaultBackground ? null : "100vh")};
  display: flex;
  align-items: center;
  flex-direction: column;
  justify-content: center;
  padding: 40px 0 0;
  box-sizing: border-box;
  overflow: hidden;
`;

const CardContainer = styled(motion.div)`
  ${pageWidth}
  border-radius: 22px;
  box-shadow: ${(props) =>
    props.$hideCardStyles ? "none" : "2px 2px 8px rgba(0, 0, 0, 0.1)"};
  background: ${(props) => (props.$hideCardStyles ? "transparent" : "#f7f7f7")};
  box-sizing: border-box;
  margin: 0 auto 10px;
  position: relative;
  box-sizing: border-box;
  margin-bottom: 86px;
`;
